<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in allowedRoutes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children || !route.name ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            :data-tour="tourAnchor(route)"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                <span class="flex items-center gap-1">
                  {{ t(route.displayName) }}
                  <VaIcon
                    v-if="!route.children && isLocked(route)"
                    aria-label="Community-plan feature"
                    name="mso-lock"
                    size="14px"
                    :color="iconColor(route)"
                  />
                </span>
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="childRoute.name ? { name: childRoute.name } : undefined"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(childRoute.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="flex items-center gap-1 leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                  <VaIcon
                    v-if="isLocked(childRoute)"
                    aria-label="Community-plan feature"
                    name="mso-lock"
                    size="14px"
                    :color="textColor(childRoute)"
                  />
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { useAuthStore } from '../../stores/auth'
import { useSettingsStore } from '../../stores/settings'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()

    const value = ref<boolean[]>([])
    const authStore = useAuthStore()
    const settingsStore = useSettingsStore()

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => Boolean(child.name && route.name === child.name)

    const routeHasActiveChild = (section: INavigationRoute): boolean => {
      if (section.children?.length) {
        return section.children.some((child) => routeHasActiveChild(child))
      }
      return Boolean(section.name && route.name === section.name)
    }

    const filterRoutesByRole = (routes: INavigationRoute[], role?: string): INavigationRoute[] => {
      return routes
        .map((route) => {
          const roles = route.meta?.roles
          const allowedByRole = !roles || roles.length === 0 || (role ? roles.includes(role) : false)
          if (!allowedByRole) return null

          if (!route.children?.length) return route
          const children = filterRoutesByRole(route.children, role)
          if (children.length === 0) return null
          return { ...route, children }
        })
        .filter((route): route is INavigationRoute => route !== null)
    }

    // Streets and Buildings are mutually exclusive per community mode: hide whichever
    // doesn't apply so the Maintenance section only shows the relevant nav entry.
    const filterRoutesByMode = (routes: INavigationRoute[], mode: string): INavigationRoute[] => {
      return routes
        .map((route) => {
          if (route.name === 'streets' && mode === 'CONDOMINIUM') return null
          if (route.name === 'buildings' && mode !== 'CONDOMINIUM') return null

          if (!route.children?.length) return route
          const children = filterRoutesByMode(route.children, mode)
          if (children.length === 0) return null
          return { ...route, children }
        })
        .filter((route): route is INavigationRoute => route !== null)
    }

    const allowedRoutes = computed(() => {
      const role = authStore.user?.role
      const mode = settingsStore.data?.communityType ?? 'SUBDIVISION'
      return filterRoutesByMode(filterRoutesByRole(navigationRoutes.routes, role), mode)
    })

    const setActiveExpand = () =>
      (value.value = allowedRoutes.value.map((route: INavigationRoute) => routeHasActiveChild(route)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    // Deep green sidebar with light text (swap SIDEBAR_BG to a navy like '#1E3A5F' for blue)
    const SIDEBAR_BG = '#166534'
    const color = computed(() => SIDEBAR_BG)
    const activeColor = computed(() => 'rgba(255, 255, 255, 0.16)')

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? '#FFFFFF' : 'rgba(255, 255, 255, 0.72)')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? '#FFFFFF' : 'rgba(255, 255, 255, 0.80)')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    // Gated nav items stay visible (so free tenants see what Community unlocks) but get a
    // small lock badge. Clicking them still navigates normally — the router's beforeEach
    // guard (see router/index.ts) redirects to the Upgrade-locked page for a locked tenant.
    const isLocked = (route: INavigationRoute) =>
      route.meta?.requiresPlan === 'COMMUNITY' && !authStore.featuresUnlocked

    // Stable anchors for the onboarding tour (driver.js targets [data-tour="…"]).
    const tourAnchorMap: Record<string, string> = {
      'menu.sectionCommunity': 'nav-community',
      'menu.sectionDirectory': 'nav-directory',
      'menu.sectionBilling': 'nav-billing',
      'menu.sectionGateVisitors': 'nav-gate',
      'menu.sectionOversight': 'nav-oversight',
      'menu.sectionAdministration': 'nav-admin',
      'menu.sectionHomeownerFinance': 'nav-my-finance',
      'menu.sectionHomeownerAccess': 'nav-my-access',
      'menu.sectionHomeownerCommunity': 'nav-my-community',
    }
    const tourAnchor = (route: INavigationRoute) => tourAnchorMap[route.displayName]

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      allowedRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      tourAnchor,
      isLocked,
    }
  },
})
</script>
